/**
 * Глобальная панель ИИ-агента (ADR-0010, И1): кнопка вызова в оболочке и
 * выезжающая справа панель беседы.
 *
 * Беседы живут на сервере (agroai-6al.16): открытие панели продолжает
 * последний чат, «История» — список прошлых чатов, чат можно закрепить
 * (хранится бессрочно) или удалить; незакреплённые сервер удаляет через
 * `ttl_days` после последней активности. Ответ сервер дописывает в историю
 * сам, по окончании потока (и при обрыве — то, что успело прийти).
 *
 * Вид (agroai-6al.17) — как composer Claude Code: поле ввода во всю ширину,
 * отправка внутри поля справа снизу, режимы — слева снизу; ход работы агента
 * — строка «Сейчас: …» и свёрнутый «Ход работы · N шагов · 42 с».
 *
 * «Авто» (agroai-6al.26) — режим по умолчанию: сервер сам решает, ответить
 * числом, показать экран, повести туром или собрать отчёт. Решение видно
 * строкой над ответом («Авто → Отчёт · доклад по сезону») с кнопкой
 * «изменить» — тот же вопрос заново в явном режиме.
 */

import { useChat, Chat } from '@ai-sdk/react';
import { DefaultChatTransport, type DynamicToolUIPart, type UIMessage } from 'ai';
import {
  ArrowUp,
  Copy,
  FileChartColumn,
  Lightbulb,
  Paperclip,
  MessageSquare,
  RotateCcw,
  Route,
  Sparkles,
  Square,
  WandSparkles,
  X,
} from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ClipboardEvent, type DragEvent, type FormEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AI_REPORT_PARAM } from './report/savedReports';
import { Streamdown } from 'streamdown';
import { useOptionalAuth } from '../auth/context';
import { Icon } from '../astra/ui/Icon';
import { useAgentSnapshot, type PageContext } from './AgentContext';
import { screen as screenImages, toAttachments, type Attachment } from './attachments';
import { CLIENT_UI_TOOLS, UI_TOOL_NAMES, UiToolCard } from './UiToolCard';
import { StatusLine, WorkLog, type WorkStep } from './WorkLog';
import { chatTitle as shownTitle, clarifyChat, createChat, csrfToken, deleteChat, getChat, listChats, patchChat, stopChat, type ChatSummary } from './history';
import { clearHighlights, openTourStep, pageForPath, runCommand, type UiCommand } from '../ai-surface/runtime';
import { screenTitle } from './screenTitles';
import { autoLabel, type AutoParts } from './autoLabel';
import { usePersona } from '../astra/auth/RoleContext';
import { PanelResizeHandle, usePanelWidth } from './PanelResize';
import { PanelHeader, type PanelSize } from './PanelHeader';
import { ChatDrawer, type DrawerTab } from './ChatDrawer';
import { ModePicker } from './ModePicker';
import { screenQuestions } from './screenHints';
import { ChoiceCard, TourBar, TourCard, isChoice, isTour, type TourPlan, type TourPosition } from './TourCard';
import { isReport, type Report } from './report/types';
import { defineMessages, type PlainMessage } from '../i18n/define';
import { useT } from '../i18n/hooks';
import { isSourceLocale, tr } from '../i18n/runtime';

const m = defineMessages({
  sAutoPending: { id: 'ai.panel.suggest.auto.pending', defaultMessage: 'Сколько хозяйств ещё не приступили к сбору хлопка?', description: 'Пример вопроса к ИИ (кнопка-подсказка в пустой беседе, режим «Авто»); нажатие отправляет текст как вопрос' },
  sAutoWater: { id: 'ai.panel.suggest.auto.water', defaultMessage: 'Где посмотреть воду по хозяйствам?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Авто»)' },
  sAutoTour: { id: 'ai.panel.suggest.auto.tour', defaultMessage: 'Проведи меня по платформе', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Авто»): просьба провести тур' },
  sAutoReport: { id: 'ai.panel.suggest.auto.report', defaultMessage: 'Сделай отчёт для доклада хокиму по сезону хлопка', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Авто»): просьба собрать отчёт для хокима' },
  sAnswerPending: { id: 'ai.panel.suggest.answer.pending', defaultMessage: 'Сколько хозяйств ещё не приступили к сбору хлопка?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Ответ»)' },
  sAnswerOverdue: { id: 'ai.panel.suggest.answer.overdue', defaultMessage: 'Где работы просрочены и на какой площади?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Ответ»)' },
  sAnswerDelivered: { id: 'ai.panel.suggest.answer.delivered', defaultMessage: 'Сколько хлопка сдано против Плана сдачи?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Ответ»); «План сдачи» — название плана заготовки' },
  sHintNotStarted: { id: 'ai.panel.suggest.hint.notStarted', defaultMessage: 'Кто ещё не начал Сбор №1?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Подсказка»); «Сбор №1» — первый сбор хлопка' },
  sHintWater: { id: 'ai.panel.suggest.hint.water', defaultMessage: 'Где посмотреть баланс воды по хлопку?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Подсказка»)' },
  sHintOverdue: { id: 'ai.panel.suggest.hint.overdue', defaultMessage: 'Какие хозяйства больше всего после срока?', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Подсказка»)' },
  sGuideIntro: { id: 'ai.panel.suggest.guide.intro', defaultMessage: 'Проведи мне ознакомление с платформой: какие есть разделы и что на них смотреть', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Гид»): просьба провести ознакомительный тур' },
  sGuideWhere: { id: 'ai.panel.suggest.guide.where', defaultMessage: 'Покажи, где видно, кто не начал сбор хлопка', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Гид»)' },
  sGuideWater: { id: 'ai.panel.suggest.guide.water', defaultMessage: 'Открой воду', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Гид»): просьба открыть экран воды; коротко' },
  sReportCotton: { id: 'ai.panel.suggest.report.cotton', defaultMessage: 'Отчёт о сборе хлопка: кто не приступил, темп по дням, сдача против Плана сдачи', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Отчёт»)' },
  sReportWater: { id: 'ai.panel.suggest.report.water', defaultMessage: 'Отчёт по воде: заявки против подачи, где дефицит и что с поливами', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Отчёт»)' },
  sReportQuarantine: { id: 'ai.panel.suggest.report.quarantine', defaultMessage: 'Отчёт по карантину: очаги и предписания после срока', description: 'Пример вопроса к ИИ (кнопка-подсказка, режим «Отчёт»)' },
  reasonRelogin: { id: 'ai.panel.reason.relogin', defaultMessage: 'нужно войти заново', description: 'Причина сбоя ответа ИИ после «Не получилось: …», строчными: сессия закончилась' },
  reasonNoConnection: { id: 'ai.panel.reason.noConnection', defaultMessage: 'нет связи с сервером', description: 'Причина сбоя ответа ИИ после «Не получилось: …», строчными: нет связи' },
  reasonServer: { id: 'ai.panel.reason.server', defaultMessage: 'сервер не смог обработать вопрос', description: 'Причина сбоя ответа ИИ после «Не получилось: …», строчными: ошибка сервера' },
  modeAuto: { id: 'ai.panel.mode.auto', defaultMessage: 'Авто', description: 'Режим ИИ-помощника, кнопка ряда режимов; коротко' },
  modeAutoHint: { id: 'ai.panel.mode.autoHint', defaultMessage: 'Сам решит: ответ числом, экран, тур или отчёт', description: 'Подсказка режима «Авто»' },
  modeAnswer: { id: 'ai.panel.mode.answer', defaultMessage: 'Ответ', description: 'Режим ИИ-помощника, кнопка ряда режимов; коротко' },
  modeAnswerHint: { id: 'ai.panel.mode.answerHint', defaultMessage: 'Просто ответить по данным', description: 'Подсказка режима «Ответ»' },
  modeHint: { id: 'ai.panel.mode.hint', defaultMessage: 'Подсказка', description: 'Режим ИИ-помощника, кнопка ряда режимов (ответить и показать, где это на экране); коротко' },
  modeHintHint: { id: 'ai.panel.mode.hintHint', defaultMessage: 'Ответить и показать экран, где это видно', description: 'Подсказка режима «Подсказка»' },
  modeGuide: { id: 'ai.panel.mode.guide', defaultMessage: 'Гид', description: 'Режим ИИ-помощника, кнопка ряда режимов (ведёт по экранам); коротко' },
  modeGuideHint: { id: 'ai.panel.mode.guideHint', defaultMessage: 'Провести по экранам: открыть, отфильтровать, подсветить', description: 'Подсказка режима «Гид»' },
  modeReport: { id: 'ai.panel.mode.report', defaultMessage: 'Отчёт', description: 'Режим ИИ-помощника, кнопка ряда режимов (собирает отчёт); коротко' },
  modeReportHint: { id: 'ai.panel.mode.reportHint', defaultMessage: 'Многошаговый анализ и отчётная страница из компонентов', description: 'Подсказка режима «Отчёт»' },
  taskLookup: { id: 'ai.panel.task.lookup', defaultMessage: 'справка', description: 'Тип вопроса в подписи маршрута над ответом ИИ: справка; строчными' },
  taskNavigate: { id: 'ai.panel.task.navigate', defaultMessage: 'навигация', description: 'Тип вопроса в подписи маршрута над ответом ИИ: навигация по экранам; строчными' },
  taskAnalysis: { id: 'ai.panel.task.analysis', defaultMessage: 'анализ', description: 'Тип вопроса в подписи маршрута над ответом ИИ: анализ данных; строчными' },
  taskReport: { id: 'ai.panel.task.report', defaultMessage: 'отчёт', description: 'Тип вопроса в подписи маршрута над ответом ИИ: отчёт; строчными' },
  decidedBy: { id: 'ai.panel.route.decidedBy', defaultMessage: 'решили: {by}', description: 'Подсказка над ответом в режиме «Авто»: кто выбрал режим; строчными; {by} — кто' },
  locked: { id: 'ai.panel.route.locked', defaultMessage: '{label} · режим закреплён', description: 'Подпись решения «Авто» над ответом, когда режим (отчёт/гид) закреплён за беседой; {label} — прежняя подпись решения' },
  change: { id: 'ai.panel.route.change', defaultMessage: 'изменить', description: 'Кнопка у решения «Авто» над ответом: повторить вопрос в другом режиме; строчными, коротко' },
  retryInMode: { id: 'ai.panel.route.retryInMode', defaultMessage: 'Повторить в режиме', description: 'Подпись для диктора ряда кнопок выбора режима для повтора вопроса' },
  routeTitle: { id: 'ai.panel.route.title', defaultMessage: 'Маршрут задачи: режим, тип вопроса и модель', description: 'Подсказка подписи «режим · тип вопроса» над ответом ИИ' },
  routeLine: { id: 'ai.panel.route.line', defaultMessage: '{mode} · {task}', description: 'Подпись над ответом ИИ: режим и тип вопроса' },
  newChat: { id: 'ai.panel.newChat', defaultMessage: 'Новая беседа', description: 'Название беседы в шапке панели ИИ, пока беседа не начата' },
  interrupted: { id: 'ai.panel.failure.interrupted', defaultMessage: 'Прервано вами', description: 'Плашка под беседой: участник сам остановил ответ' },
  failedWith: { id: 'ai.panel.failure.failedWith', defaultMessage: 'Не получилось: {reason}', description: 'Плашка ошибки под беседой; {reason} — причина словами, строчными' },
  failedCut: { id: 'ai.panel.failure.cut', defaultMessage: 'Не получилось: связь прервалась, не дождавшись конца ответа', description: 'Плашка ошибки под беседой: поток ответа оборвался' },
  failedSilent: { id: 'ai.panel.failure.silent', defaultMessage: 'Не получилось: нет ответа от сервера', description: 'Плашка ошибки под беседой: сервер долго молчит, ожидание прекращено' },
  failedOrphan: { id: 'ai.panel.failure.orphan', defaultMessage: 'Ответ не был получен (страница обновлялась или связь прервалась)', description: 'Плашка ошибки под беседой: вопрос остался без ответа' },
  reportEditNotice: { id: 'ai.panel.notice.reportEdit', defaultMessage: 'Правки пойдут в сохранённый отчёт: новая версия появится на его странице.', description: 'Уведомление над полем ввода при правке сохранённого отчёта с помощью ИИ' },
  chatOpenFailed: { id: 'ai.panel.notice.chatOpenFailed', defaultMessage: 'Чат не открылся: {reason}', description: 'Ошибка в списке бесед; {reason} — причина' },
  pinFailed: { id: 'ai.panel.notice.pinFailed', defaultMessage: 'Не удалось: {reason}', description: 'Уведомление над полем ввода: действие над беседой (закрепить/открепить) не удалось; {reason} — причина' },
  renameFailed: { id: 'ai.panel.notice.renameFailed', defaultMessage: 'Не переименовано: {reason}', description: 'Уведомление над полем ввода: беседа не переименована; {reason} — причина' },
  deleteFailed: { id: 'ai.panel.notice.deleteFailed', defaultMessage: 'Беседа не удалена: {reason}', description: 'Уведомление над полем ввода: беседа не удалена; {reason} — причина' },
  imageReadFailed: { id: 'ai.panel.notice.imageReadFailed', defaultMessage: 'Не удалось прочитать картинку.', description: 'Уведомление над полем ввода: файл-картинка не прочитался' },
  screenshotQuestion: { id: 'ai.panel.screenshotQuestion', defaultMessage: 'Посмотри на скриншот: что на нём и что это значит по данным?', description: 'Вопрос по умолчанию, который уходит ИИ, когда участник приложил картинку без текста; виден в беседе как реплика участника' },
  historyDown: { id: 'ai.panel.notice.historyDown', defaultMessage: 'История чатов сейчас недоступна — этот ответ не сохранится.', description: 'Уведомление над полем ввода: история бесед недоступна' },
  clarifyAccepted: { id: 'ai.panel.notice.clarifyAccepted', defaultMessage: 'Уточнение принято: ИИ учтёт его на следующем шаге.', description: 'Уведомление над полем ввода: уточнение к идущему ответу принято' },
  clarifyTooLate: { id: 'ai.panel.notice.clarifyTooLate', defaultMessage: 'Ответ уже закончился — отправьте это отдельным вопросом.', description: 'Уведомление над полем ввода: уточнение опоздало, ответ закончился' },
  clarifyFailed: { id: 'ai.panel.notice.clarifyFailed', defaultMessage: 'Уточнение не отправлено: {reason}', description: 'Уведомление над полем ввода: уточнение не отправилось; {reason} — причина' },
  copied: { id: 'ai.panel.notice.copied', defaultMessage: 'Ответ скопирован.', description: 'Уведомление над полем ввода: ответ ИИ скопирован в буфер' },
  copyFailed: { id: 'ai.panel.notice.copyFailed', defaultMessage: 'Не удалось скопировать ответ.', description: 'Уведомление над полем ввода: копирование ответа не удалось' },
  openFabAria: { id: 'ai.panel.fab.aria', defaultMessage: 'Открыть ИИ-аналитика', description: 'Подпись для диктора плавающей кнопки вызова панели ИИ' },
  openFab: { id: 'ai.panel.fab.label', defaultMessage: 'Спросить ИИ', description: 'Текст плавающей кнопки вызова панели ИИ (все роли); коротко' },
  panelAria: { id: 'ai.panel.aria', defaultMessage: 'ИИ-аналитик', description: 'Подпись для диктора панели ИИ-помощника' },
  empty: { id: 'ai.panel.empty', defaultMessage: 'Спросите о данных района — ответ придёт потоком, числа — из запросов к данным.', description: 'Приглашение в пустой беседе с ИИ' },
  attachedImage: { id: 'ai.panel.attachedImage', defaultMessage: 'Приложенная картинка', description: 'Альтернативный текст картинки, приложенной к вопросу, если у файла нет имени' },
  imageNote: { id: 'ai.panel.imageNote', defaultMessage: 'Изображение', description: 'Заметка в беседе вместо картинки, которую нельзя показать (из истории)' },
  imageNoteNamed: { id: 'ai.panel.imageNoteNamed', defaultMessage: 'Изображение: {name}', description: 'Заметка в беседе вместо картинки, которую нельзя показать; {name} — имя файла' },
  artifactVersion: { id: 'ai.panel.artifact.version', defaultMessage: 'версия {n}', description: 'Подпись карточки-отчёта в беседе: номер версии; строчными' },
  artifactOpen: { id: 'ai.panel.artifact.open', defaultMessage: 'Открыть', description: 'Кнопка в карточке-отчёте в беседе: открыть отчёт справа' },
  answerTools: { id: 'ai.panel.tools.aria', defaultMessage: 'Действия с ответом', description: 'Подпись для диктора кнопок под ответом ИИ (копировать, повторить)' },
  copyAnswerAria: { id: 'ai.panel.tools.copyAria', defaultMessage: 'Копировать ответ', description: 'Подпись для диктора кнопки под ответом ИИ' },
  copyAnswerHint: { id: 'ai.panel.tools.copyHint', defaultMessage: 'Копировать текст', description: 'Подсказка кнопки копирования под ответом ИИ' },
  repeatAria: { id: 'ai.panel.tools.repeatAria', defaultMessage: 'Повторить вопрос', description: 'Подпись для диктора кнопки под ответом ИИ' },
  repeatHint: { id: 'ai.panel.tools.repeatHint', defaultMessage: 'Задать тот же вопрос ещё раз', description: 'Подсказка кнопки повтора под ответом ИИ' },
  answerInterrupted: { id: 'ai.panel.answerInterrupted', defaultMessage: 'Ответ прерван — сохранено то, что успело прийти.', description: 'Заметка под ответом ИИ, который был прерван' },
  stillPreparing: { id: 'ai.panel.stillPreparing', defaultMessage: 'Ответ ещё готовится · {seconds} с', description: 'Статус после обновления страницы: ответ на сервере ещё готовится; {seconds} — сколько секунд прошло' },
  stalled: { id: 'ai.panel.stalled', defaultMessage: 'Ответ готовится дольше обычного…', description: 'Заметка: ответ идёт дольше обычного' },
  retry: { id: 'ai.panel.retry', defaultMessage: 'Повторить', description: 'Кнопка в плашке ошибки: повторить последний вопрос; коротко' },
  screenCtx: { id: 'ai.panel.screenCtx', defaultMessage: 'Экран: {title}', description: 'Метка над полем ввода: вопрос уйдёт с контекстом этого экрана; {title} — название экрана' },
  removeScreenAria: { id: 'ai.panel.removeScreenAria', defaultMessage: 'Убрать экран из вопроса', description: 'Подпись для диктора кнопки «×» у метки экрана' },
  removeScreenHint: { id: 'ai.panel.removeScreenHint', defaultMessage: 'Спросить без контекста этой страницы', description: 'Подсказка кнопки «×» у метки экрана' },
  attachedAria: { id: 'ai.panel.attachedAria', defaultMessage: 'Приложенные картинки', description: 'Подпись для диктора списка приложенных картинок' },
  pictureAlt: { id: 'ai.panel.pictureAlt', defaultMessage: 'Картинка', description: 'Альтернативный текст миниатюры приложенной картинки без имени' },
  removePicture: { id: 'ai.panel.removePicture', defaultMessage: 'Убрать картинку {name}', description: 'Подпись для диктора кнопки «×» у миниатюры; {name} — имя файла (может быть пустым)' },
  inputBusy: { id: 'ai.panel.input.busy', defaultMessage: 'Уточните, пока ИИ отвечает…', description: 'Подсказка в поле ввода, пока ИИ отвечает' },
  inputIdle: { id: 'ai.panel.input.idle', defaultMessage: 'Спросите о данных района…', description: 'Подсказка в поле ввода вопроса к ИИ' },
  inputAria: { id: 'ai.panel.input.aria', defaultMessage: 'Вопрос ИИ-аналитику', description: 'Подпись для диктора поля ввода вопроса' },
  pickImages: { id: 'ai.panel.pickImages', defaultMessage: 'Выбрать картинки', description: 'Подпись для диктора скрытого поля выбора файлов-картинок' },
  attachAria: { id: 'ai.panel.attachAria', defaultMessage: 'Приложить картинку', description: 'Подпись для диктора кнопки-скрепки' },
  attachHint: { id: 'ai.panel.attachHint', defaultMessage: 'Приложить скриншот (PNG, JPEG, WebP; до 3 штук, до 5 МБ) — или вставьте из буфера, или перетащите', description: 'Подсказка кнопки-скрепки: какие картинки можно приложить' },
  clarify: { id: 'ai.panel.clarify', defaultMessage: 'Уточнить', description: 'Кнопка во время ответа ИИ: отправить поправку к идущему ответу; коротко' },
  clarifyHint: { id: 'ai.panel.clarifyHint', defaultMessage: 'Поправить ответ, пока он готовится', description: 'Подсказка кнопки «Уточнить»' },
  stop: { id: 'ai.panel.stop', defaultMessage: 'Остановить', description: 'Подпись и подсказка кнопки остановки ответа ИИ' },
  send: { id: 'ai.panel.send', defaultMessage: 'Отправить', description: 'Подпись для диктора кнопки отправки вопроса' },
  sendHint: { id: 'ai.panel.sendHint', defaultMessage: 'Отправить (Enter)', description: 'Подсказка кнопки отправки вопроса; Enter — клавиша' },
});

/* Отчёт тянет графики и карту — отдельным чанком, только когда он есть. */
const ReportCanvas = lazy(() => import('./report/ReportCanvas').then((m) => ({ default: m.ReportCanvas })));
import './agent.css';
import './agent-chrome.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const AGENT_ENDPOINT = `${(import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')}/ai/agent/chat`;

/** Потолок высоты таблицы и кода в ответе: длинное — прокруткой внутри блока. */
export const TABLE_MAX_HEIGHT = 'var(--agent-table-max, 360px)';

/** Прежнее хранилище беседы во вкладке (до 6al.16) — стирается при входе. */
const LEGACY_PREFIX = 'agroai-agent-chat-v1:';

/** Примеры вопросов по режиму. Массивы не ранжируются (журнал №85). */
const SUGGESTIONS: Record<string, PlainMessage[]> = {
  auto: [m.sAutoPending, m.sAutoWater, m.sAutoTour, m.sAutoReport],
  answer: [m.sAnswerPending, m.sAnswerOverdue, m.sAnswerDelivered],
  hint: [m.sHintNotStarted, m.sHintWater, m.sHintOverdue],
  guide: [m.sGuideIntro, m.sGuideWhere, m.sGuideWater],
  report: [m.sReportCotton, m.sReportWater, m.sReportQuarantine],
};

/** Режимы ассистента (ADR-0011); «Авто» — по умолчанию (agroai-6al.26). */
export type AssistantMode = 'auto' | 'answer' | 'hint' | 'guide' | 'report';
/** Явные режимы — на них можно переиграть решение «Авто». */
export type ExplicitMode = Exclude<AssistantMode, 'auto'>;

/**
 * Продолжать ли беседу автоматически: в последнем шаге ответа есть действия
 * интерфейса, исполненные БРАУЗЕРОМ (не командами claude_code), и все они
 * завершены. Серверные инструменты сюда не входят — иначе ответ режима
 * claude_code (все вызовы в одном шаге) переотправлялся бы бесконечно.
 */
export function shouldContinue({ messages }: { messages: UIMessage[] }): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return false;
  const start = last.parts.reduce((at, part, index) => (part.type === 'step-start' ? index : at), -1);
  const ui = last.parts
    .slice(start + 1)
    .filter((part): part is DynamicToolUIPart => part.type === 'dynamic-tool' && CLIENT_UI_TOOLS.has(part.toolName))
    .filter((part) => !part.providerExecuted);
  return ui.length > 0 && ui.every((part) => part.state === 'output-available' || part.state === 'output-error');
}

interface ChatDeps {
  snapshot: () => PageContext;
  mode: () => AssistantMode;
  chatId: () => string | null;
  navigate: (href: string) => void;
  /** Живость потока (agroai-6al.58): пришёл кусок / поток закрылся (с `finish` или без). */
  onActivity: () => void;
  onStreamEnd: (finished: boolean) => void;
  /** Видимый ход работы (6al.62, Г9): что модель сейчас обдумывает — временная часть потока. */
  onProgress?: (text: string) => void;
}

const FINISH_CHUNK = /"type"\s*:\s*"finish"/;

/** Оборачивает ответ потока: видит активность и закрытие без `finish`. */
function watchStream(response: Response, deps: Pick<ChatDeps, 'onActivity' | 'onStreamEnd'>): Response {
  if (!response.body) return response;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let tail = '';
  let finished = false;
  deps.onActivity();
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      const { done, value } = await reader.read();
      if (done) {
        deps.onStreamEnd(finished);
        controller.close();
        return;
      }
      deps.onActivity();
      tail = (tail + decoder.decode(value, { stream: true })).slice(-4096);
      if (FINISH_CHUNK.test(tail)) finished = true;
      controller.enqueue(value);
    },
    cancel: (reason) => reader.cancel(reason),
  });
  return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
}

/** Причина словами: без технических деталей, без названия модели и провайдера. */
function failureReason(error: Error | undefined): string {
  const message = error?.message ?? '';
  if (/[а-яё]/i.test(message) && message.length < 200) return message;
  if (/401|403|csrf|forbidden|unauthori/i.test(message)) return tr(m.reasonRelogin);
  if (/failed to fetch|network|load failed|abort|fetch/i.test(message) || message === '') return tr(m.reasonNoConnection);
  return tr(m.reasonServer);
}

const STALL_AFTER_MS = 120_000;
const GIVE_UP_AFTER_MS = 300_000;
const RECHECK_EVERY_MS = 30_000;

function createAgentChat(id: string, deps: ChatDeps): Chat<UIMessage> {
  const chat: Chat<UIMessage> = new Chat<UIMessage>({
    id,
    messages: [],
    transport: new DefaultChatTransport<UIMessage>({
      api: AGENT_ENDPOINT,
      credentials: 'same-origin',
      fetch: async (input, init) => watchStream(await globalThis.fetch(input, init), deps),
      headers: (): Record<string, string> => {
        const token = csrfToken();
        return token ? { 'X-CSRFToken': token } : {};
      },
      body: () => ({ page_context: deps.snapshot(), mode: deps.mode(), chat_id: deps.chatId() }),
    }),
    sendAutomaticallyWhen: shouldContinue,
    // Временные части `data-progress` (сервер с AGROAI_AGENT_PROGRESS=1): в сообщение не пишутся.
    onData: (part) => {
      if (part.type !== 'data-progress') return;
      const text = (part.data as { text?: unknown } | undefined)?.text;
      if (typeof text === 'string' && text.trim()) deps.onProgress?.(text.trim());
    },
    // Действие интерфейса (режим «Гид», ключ API): сервер остановил прогон,
    // браузер исполняет проверенную команду и отдаёт результат — по нему
    // `shouldContinue` присылает продолжение. Без await: поток не ждёт перехода.
    onToolCall: ({ toolCall }) => {
      if (!CLIENT_UI_TOOLS.has(toolCall.toolName)) return;
      const command = (toolCall.input as { command?: UiCommand } | undefined)?.command;
      const tool = toolCall.toolName;
      const toolCallId = toolCall.toolCallId;
      if (!command) {
        // i18n-ignore: уходит модели (ошибка инструмента, модель исправляет вызов)
        void chat.addToolOutput({ tool, toolCallId, state: 'output-error', errorText: 'команда не проверена сервером' });
        return;
      }
      void runCommand(command, deps.navigate).then(
        (output) => chat.addToolOutput({ tool, toolCallId, output }),
        (error: unknown) =>
          chat.addToolOutput({ tool, toolCallId, state: 'output-error', errorText: String(error) }),
      );
    },
  });
  return chat;
}

const MODE_KEY = 'agroai-agent-mode';

/** Переключатель режимов: подпись, подсказка и иконка. */
export const MODES: { id: AssistantMode; label: PlainMessage; hint: PlainMessage; icon: typeof Lightbulb }[] = [
  { id: 'auto', label: m.modeAuto, hint: m.modeAutoHint, icon: WandSparkles },
  { id: 'answer', label: m.modeAnswer, hint: m.modeAnswerHint, icon: MessageSquare },
  { id: 'hint', label: m.modeHint, hint: m.modeHintHint, icon: Lightbulb },
  { id: 'guide', label: m.modeGuide, hint: m.modeGuideHint, icon: Route },
  { id: 'report', label: m.modeReport, hint: m.modeReportHint, icon: FileChartColumn },
];

const TASK_LABELS: Record<string, PlainMessage> = {
  lookup: m.taskLookup,
  navigate: m.taskNavigate,
  analysis: m.taskAnalysis,
  report: m.taskReport,
};
const DONE_KEY = 'agroai-agent-ui-done';

const isMode = (value: unknown): value is AssistantMode => MODES.some((item) => item.id === value);

function readMode(): AssistantMode {
  try {
    const value = sessionStorage.getItem(MODE_KEY);
    return isMode(value) ? value : 'auto';
  } catch {
    return 'auto';
  }
}

/** Решение «Авто» из метаданных ответа (сервер, `ai_agent/auto.py`). */
interface AutoDecision extends AutoParts {
  label: string;
  mode: string;
  by?: string;
  confidence?: number;
  rationale?: string;
}

interface MessageMeta {
  mode?: string;
  auto?: AutoDecision;
  task?: string;
  model?: string;
  interrupted?: boolean;
  /** Из истории сервера: шаг → [начало, конец], мс от начала ответа. */
  timing?: Record<string, number[]>;
  duration_ms?: number;
  /** Оценка ответа участником (agroai-6al.61). */
  feedback?: 'up' | 'down';
}

const metaOf = (message: UIMessage): MessageMeta =>
  typeof message.metadata === 'object' && message.metadata !== null ? (message.metadata as MessageMeta) : {};

function RouteCaption({
  meta,
  onRetry,
}: {
  meta: MessageMeta;
  /** Повторить вопрос в явном режиме (кнопка «изменить» у решения «Авто»). */
  onRetry?: (mode: ExplicitMode) => void;
}) {
  const t = useT();
  const [choosing, setChoosing] = useState(false);
  if (typeof meta.task !== 'string') return null;
  // Название модели не показываем (решение владельца 05.10) — только задачу.
  const task = TASK_LABELS[meta.task] ? t(TASK_LABELS[meta.task]!) : meta.task;
  const decision = meta.auto;
  if (decision && typeof decision.label === 'string') {
    // Строку и причину сервер пишет по-русски: на другом языке строка собирается из частей
    // решения (`autoLabel`), а причина — служебный текст маршрутизатора — не показывается.
    const source = isSourceLocale();
    const label = source ? decision.label : (autoLabel(decision) ?? decision.label);
    const why = [source ? decision.rationale : '', decision.by ? t(m.decidedBy, { by: decision.by }) : '', task].filter(Boolean).join(' · ');
    return (
      <div className="agent-msg__route agent-auto" title={why}>
        <span className="agent-auto__label">
          {decision.mode === 'report' || decision.mode === 'guide' ? t(m.locked, { label }) : label}
        </span>
        {onRetry ? (
          <button
            type="button"
            className="agent-auto__change focus-ring"
            aria-expanded={choosing}
            onClick={() => setChoosing((value) => !value)}
          >
            {t(m.change)}
          </button>
        ) : null}
        {choosing && onRetry ? (
          <span className="agent-auto__modes" role="group" aria-label={t(m.retryInMode)}>
            {MODES.filter((item) => item.id !== 'auto').map((item) => (
              <button
                key={item.id}
                type="button"
                className="agent-chip agent-auto__mode focus-ring"
                title={t(item.hint)}
                onClick={() => {
                  setChoosing(false);
                  onRetry(item.id as ExplicitMode);
                }}
              >
                {t(item.label)}
              </button>
            ))}
          </span>
        ) : null}
      </div>
    );
  }
  const modeMessage = MODES.find((item) => item.id === meta.mode)?.label;
  const mode = modeMessage ? t(modeMessage) : meta.mode;
  return (
    <div className="agent-msg__route" title={t(m.routeTitle)}>
      {t(m.routeLine, { mode: mode ?? '', task })}
    </div>
  );
}

function readDone(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DONE_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

const isWork = (part: UIMessage['parts'][number]): part is DynamicToolUIPart =>
  part.type === 'dynamic-tool' && !UI_TOOL_NAMES.has(part.toolName);

interface Timing {
  start: number;
  end?: number;
}

const errorText = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

/** Один отчёт беседы: все его версии (сборка и правки) — одно семейство. */
export interface ReportFamily {
  key: string;
  /** Последняя версия — её и показывает карточка и холст. */
  report: Report;
  toolCallId: string;
}

const isReportPart = (part: UIMessage['parts'][number]): boolean =>
  part.type === 'dynamic-tool' && (part.toolName === 'report_build' || part.toolName === 'report_edit') && part.state === 'output-available' && isReport(part.output);

export const familyKey = (report: Report, toolCallId: string): string =>
  report.saved_report?.id ?? (report as { lineage?: { root?: string | null } }).lineage?.root ?? toolCallId;

/** Отчёты беседы по семействам: правка не плодит карточек, остаётся последняя версия. */
export function reportFamilies(messages: UIMessage[]): Map<string, ReportFamily> {
  const out = new Map<string, ReportFamily>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (!isReportPart(part)) continue;
      const tool = part as DynamicToolUIPart;
      const report = tool.output as Report;
      out.set(familyKey(report, tool.toolCallId), { key: familyKey(report, tool.toolCallId), report, toolCallId: tool.toolCallId });
    }
  }
  return out;
}

export function AgentPanel() {
  const t = useT();
  const auth = useOptionalAuth();
  const principal = auth?.principal ?? null;
  const signedIn = auth === null ? true : Boolean(principal?.is_authenticated);
  const user = principal?.username ?? 'anon';
  const snapshot = useAgentSnapshot();
  const { persona } = usePersona();
  // Тур гида (6al.23): какой план активен и где в нём участник. В снимок
  // страницы уходит шаг тура — вопрос на шаге модель видит в его контексте.
  const [tour, setTour] = useState<{ plan: TourPlan; position: TourPosition } | null>(null);
  /** Метка «Экран: …» в поле ввода: снята — контекст страницы не отправляется. */
  const [includePage, setIncludePage] = useState(true);
  const includePageRef = useRef(includePage);
  includePageRef.current = includePage;
  const tourRef = useRef(tour);
  tourRef.current = tour;
  /**
   * Сохранённый отчёт, к которому привязана беседа кнопкой «Изменить с ИИ»
   * страницы отчёта (`/?ai_report=<id>`): правки `report_edit` идут в него,
   * хотя сама страница отчёта стоит вне оболочки и панели на ней нет.
   */
  const boundReportRef = useRef<string | null>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = () => {
    const base = snapshot();
    const active = tourRef.current;
    const step = active ? active.plan.steps[active.position.step] : undefined;
    if (!includePageRef.current) {
      // «Экран» снят крестиком: страница в вопрос не уходит, остаются демо-флаг, дата и роль.
      return {
        include_demo: base.include_demo,
        today: base.today,
        persona_role: persona?.role ?? null,
      } as unknown as typeof base;
    }
    const onSaved = /^\/reports\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i.exec(window.location.pathname);
    return {
      ...base,
      persona_role: persona?.role ?? null,
      saved_report_id: onSaved?.[1] ?? boundReportRef.current,
      tour:
        active && step
          ? {
              title: active.plan.title,
              step: active.position.step + 1,
              of: active.plan.steps.length,
              page: step.page,
              step_title: step.title,
              regions: step.regions.map((r) => r.id),
            }
          : null,
    };
  };

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [mode, setModeState] = useState<AssistantMode>(readMode);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Беседа сервера: id текущего чата и закреплён ли он.
  const [chatId, setChatIdState] = useState<string | null>(null);
  const chatIdRef = useRef<string | null>(null);
  const setChatId = (id: string | null) => {
    chatIdRef.current = id;
    setChatIdState(id);
  };
  const [pinned, setPinned] = useState(false);
  const [chats, setChats] = useState<ChatSummary[] | null>(null);
  const [ttlDays, setTtlDays] = useState(7);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Список сохранённых отчётов поверх беседы (кнопка «Отчёты» в шапке, agroai-6al.60). */
  /** Открытый холст: отчёт беседы (`family`) или сохранённый из списка (`saved`). */
  const [canvas, setCanvas] = useState<{ family: string | null; saved: string | null } | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('chats');
  const [chatTitle, setChatTitle] = useState(() => t(m.newChat));
  const [historyError, setHistoryError] = useState<string | null>(null);
  const loadedRef = useRef(false);
  const messagesRef = useRef<UIMessage[]>([]);

  const lastActivityRef = useRef(Date.now());
  const streamFinishedRef = useRef(true);
  const userStoppedRef = useRef(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** Ответ ещё готовится на сервере (после обновления страницы): с какого момента. */
  const [pending, setPending] = useState<{ since: number } | null>(null);
  /** Что модель сейчас обдумывает (6al.62, Г9); сбрасывается на новом вопросе. */
  const [thought, setThought] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [stalled, setStalled] = useState(false);

  const chat = useMemo(
    () =>
      createAgentChat(`agent:${user}`, {
        snapshot: () => snapshotRef.current(),
        mode: () => modeRef.current,
        chatId: () => chatIdRef.current,
        navigate: (href) => navigateRef.current(href),
        onActivity: () => {
          lastActivityRef.current = Date.now();
        },
        onStreamEnd: (finished) => {
          streamFinishedRef.current = finished;
        },
        onProgress: (text) => setThought(text),
      }),
    [user],
  );
  const { messages, sendMessage, status, stop, error, setMessages, clearError } = useChat({ chat });
  const busy = status === 'submitted' || status === 'streaming';

  // Любой конец без ответа объясняется плашкой (agroai-6al.58).
  const prevBusyRef = useRef(false);
  useEffect(() => {
    if (busy) {
      prevBusyRef.current = true;
      return;
    }
    if (!prevBusyRef.current) return;
    prevBusyRef.current = false;
    setStalled(false);
    if (userStoppedRef.current) {
      setFailure(t(m.interrupted));
    } else if (status === 'error') {
      setFailure(t(m.failedWith, { reason: failureReason(error) }));
    } else if (!streamFinishedRef.current) {
      setFailure(t(m.failedCut));
    }
  }, [busy, status, error]);

  // Сторожевой таймер: нет событий потока — сначала проверяем сохранённый ответ.
  useEffect(() => {
    if (!busy) return;
    let lastCheck = 0;
    const timer = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle < STALL_AFTER_MS) {
        lastCheck = 0;
        setStalled(false);
        return;
      }
      setStalled(true);
      if (idle >= GIVE_UP_AFTER_MS) {
        clearInterval(timer);
        setStalled(false);
        stop();
        setFailure(t(m.failedSilent));
        return;
      }
      if (idle - lastCheck < RECHECK_EVERY_MS && lastCheck !== 0) return;
      lastCheck = idle;
      const id = chatIdRef.current;
      if (!id) return;
      getChat(id).then(
        (detail) => {
          const last = detail.messages[detail.messages.length - 1];
          if (last?.role === 'assistant' && detail.messages.length >= messagesRef.current.length) {
            clearInterval(timer);
            stop();
            setStalled(false);
            setFailure(null);
            userStoppedRef.current = false;
            setMessages(detail.messages);
          }
        },
        () => undefined,
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [busy, stop, setMessages]);

  useEffect(() => {
    try {
      sessionStorage.removeItem(`${LEGACY_PREFIX}${user}`);
    } catch {
      /* нечего стирать */
    }
  }, [user]);

  // Команды claude_code (`providerExecuted`): исполняются по порядку, один
  // раз — id исполненных помнит sessionStorage, перезагрузка их не повторит.
  const doneRef = useRef<Set<string>>(readDone());
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const rememberDone = () => {
    try {
      sessionStorage.setItem(DONE_KEY, JSON.stringify([...doneRef.current].slice(-200)));
    } catch {
      /* без хранилища команда может повториться после перезагрузки — безвредно */
    }
  };
  useEffect(() => {
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      for (const part of message.parts) {
        if (part.type !== 'dynamic-tool' || !CLIENT_UI_TOOLS.has(part.toolName) || !part.providerExecuted) continue;
        if (part.state !== 'output-available' || doneRef.current.has(part.toolCallId)) continue;
        const command = (part.input as { command?: UiCommand } | undefined)?.command;
        doneRef.current.add(part.toolCallId);
        rememberDone();
        if (command) queueRef.current = queueRef.current.then(() => runCommand(command, (href) => navigateRef.current(href)));
      }
    }
  }, [messages]);

  // Время шагов — по меткам прихода событий (agroai-6al.17).
  const timesRef = useRef(new Map<string, Timing>());
  const turnRef = useRef<{ started: number; message: string | null }>({ started: Date.now(), message: null });
  // Длительность ответа — состоянием: метка ставится после отрисовки, и
  // строка «Ход работы · … с» должна перерисоваться с ней.
  const [totals, setTotals] = useState<Record<string, number>>({});
  useEffect(() => {
    const now = Date.now();
    const last = messages[messages.length - 1];
    if (busy && last?.role === 'assistant' && turnRef.current.message === null) turnRef.current.message = last.id;
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      for (const part of message.parts) {
        if (!isWork(part)) continue;
        const known = timesRef.current.get(part.toolCallId);
        const finished = part.state === 'output-available' || part.state === 'output-error';
        if (!known) {
          if (busy) timesRef.current.set(part.toolCallId, finished ? { start: now, end: now } : { start: now });
        } else if (finished && known.end === undefined) known.end = now;
      }
    }
    const finishedId = turnRef.current.message;
    if (!busy && finishedId && totals[finishedId] === undefined) {
      setTotals((all) => ({ ...all, [finishedId]: now - turnRef.current.started }));
    }
  }, [messages, busy, totals]);

  // Новый тур в ответе становится активным, но НЕ открывается сам: первый
  // переход — по нажатию участника («процессом управляю я сам»).
  const seenToursRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      for (const part of message.parts) {
        if (part.type !== 'dynamic-tool' || part.toolName !== 'ui_tour_plan' || part.state !== 'output-available') continue;
        if (seenToursRef.current.has(part.toolCallId) || !isTour(part.output)) continue;
        seenToursRef.current.add(part.toolCallId);
        clearHighlights();
        setTour({ plan: part.output, position: { id: part.toolCallId, step: 0, opened: false, missing: [] } });
      }
    }
  }, [messages]);

  const goTour = (plan: TourPlan, id: string, step: number) => {
    const target = plan.steps[step];
    if (!target) return;
    setTour({ plan, position: { id, step, opened: true, missing: [] } });
    void openTourStep(target, (href) => navigateRef.current(href)).then(({ missing }) => {
      setTour((current) =>
        current && current.position.id === id && current.position.step === step
          ? { ...current, position: { ...current.position, missing } }
          : current,
      );
    });
  };
  const closeTour = () => {
    clearHighlights();
    setTour(null);
  };

  const showChat = useCallback(
    (loaded: UIMessage[]) => {
      // Команды старого ответа уже исполнены тогда — при открытии не повторять.
      for (const message of loaded) {
        for (const part of message.parts) {
          if (part.type !== 'dynamic-tool') continue;
          doneRef.current.add(part.toolCallId);
          // Туры старых ответов — по кнопке «Пройти тур», сами не активируются.
          seenToursRef.current.add(part.toolCallId);
        }
      }
      rememberDone();
      setMessages(loaded);
    },
    [setMessages],
  );

  // Ответ готовится на сервере: ждём его опросом истории (agroai-6al.58).
  const isPending = pending !== null;
  useEffect(() => {
    if (!isPending) return;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    const poll = setInterval(() => {
      const id = chatIdRef.current;
      if (!id) return;
      getChat(id).then(
        (detail) => {
          if (chatIdRef.current !== id) return;
          showChat(detail.messages);
          if (detail.status !== 'running') setPending(null);
        },
        () => undefined,
      );
    }, 3000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [isPending, showChat]);

  const refreshList = useCallback(async () => {
    const list = await listChats();
    setChats(list.chats);
    setTtlDays(list.ttl_days);
    setHistoryError(null);
    return list.chats;
  }, []);

  // Первый ответ даёт беседе название по вопросу (его ставит сервер): после
  // ответа подтягиваем его в шапку.
  const wasBusyRef = useRef(false);
  useEffect(() => {
    const finished = wasBusyRef.current && !busy;
    wasBusyRef.current = busy;
    const id = chatIdRef.current;
    if (!finished || !id) return;
    refreshList()
      .then((list) => {
        const mine = list.find((item) => item.id === id);
        if (mine && id === chatIdRef.current) setChatTitle(shownTitle(mine.title));
      })
      .catch(() => undefined);
  }, [busy, refreshList]);

  const [open, setOpen] = useState(false);
  // Панель всегда открывается не во весь экран (05.10): «на весь экран» —
  // состояние одного показа, а не настройка. Режим «Отчёт» разворачивает её
  // сам, когда приходит отчёт.
  const [size, setSize] = useState<PanelSize>('side');
  const expanded = size === 'focus';
  const setExpanded = (value: boolean) => setSize(value ? 'focus' : 'side');
  const panelWidth = usePanelWidth(user);
  const settleWidth = panelWidth.settle;
  useEffect(() => {
    if (open) return;
    setSize('side');
    settleWidth();
  }, [open, settleWidth]);
  const [resizing, setResizing] = useState(false);

  // Открытие ассистента продолжает последний чат.
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    refreshList()
      .then(async (list) => {
        if (chatIdRef.current || list.length === 0) return;
        const latest = [...list].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
        if (!latest) return;
        const detail = await getChat(latest.id);
        if (chatIdRef.current) return; // участник успел начать новый
        setChatId(detail.id);
        setPinned(detail.pinned);
        setChatTitle(shownTitle(detail.title));
        restoreMode(detail.mode);
        showChat(detail.messages);
        setPending(detail.status === 'running' ? { since: Date.now() - (detail.elapsed_s ?? 0) * 1000 } : null);
      })
      .catch((reason: unknown) => setHistoryError(errorText(reason)));
  }, [open, refreshList, showChat]);

  // «Авто» решило собрать отчёт — панели нужна ширина, как у режима «Отчёт»;
  // решило вести по экранам — панель, развёрнутая самим «Авто», сворачивается,
  // иначе экран, на который ведёт гид, не видно.
  const widenedRef = useRef<Set<string>>(new Set());
  const autoWideRef = useRef(false);
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!busy || last?.role !== 'assistant' || widenedRef.current.has(last.id)) return;
    const decided = metaOf(last).auto?.mode;
    if (decided === 'report') {
      widenedRef.current.add(last.id);
      autoWideRef.current = true;
      setExpanded(true);
    } else if (decided === 'guide' && autoWideRef.current) {
      widenedRef.current.add(last.id);
      autoWideRef.current = false;
      setExpanded(false);
    }
  }, [messages, busy]);

  // «Липкий» режим (6al.66): «Авто» решило отчёт или гид — сегмент переключается
  // сам, и следующие сообщения беседы идут в этом режиме без повторной
  // классификации. Ответ/подсказка оставляют «Авто» (иначе следующая просьба
  // об отчёте была бы загнана в «Ответ»).
  const stuckRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!busy || last?.role !== 'assistant' || stuckRef.current.has(last.id)) return;
    const decided = metaOf(last).auto?.mode;
    if ((decided === 'report' || decided === 'guide') && modeRef.current === 'auto') {
      stuckRef.current.add(last.id);
      setModeState(decided);
      try {
        sessionStorage.setItem(MODE_KEY, decided);
      } catch {
        /* режим живёт до перезагрузки */
      }
    }
  }, [messages, busy]);

  function restoreMode(saved: string | undefined) {
    // Режим живёт в чате (6al.26): открытый чат продолжается в своём режиме.
    if (isMode(saved)) {
      setModeState(saved);
      try {
        sessionStorage.setItem(MODE_KEY, saved);
      } catch {
        /* режим живёт до перезагрузки */
      }
    }
  }

  const setMode = (next: AssistantMode) => {
    setModeState(next);
    try {
      sessionStorage.setItem(MODE_KEY, next);
    } catch {
      /* режим живёт до перезагрузки */
    }
    // «Отчёт» — полноэкранный режим: отчётной странице нужна ширина.
    if (next === 'report') setExpanded(true);
  };
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (list && typeof list.scrollTo === 'function') list.scrollTo({ top: list.scrollHeight });
  }, [messages, open, historyOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Esc: закрыть историю, затем вернуть из полноэкранного, затем закрыть панель.
      if (historyOpen) setHistoryOpen(false);
      else if (size !== 'side') setSize('side');
      else setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, size, historyOpen]);

  const startNew = () => {
    stop();
    clearError();
    setFailure(null);
    setPending(null);
    closeTour();
    setMessages([]);
    setChatId(null);
    setPinned(false);
    setCanvas(null);
    setIncludePage(true);
    setHistoryOpen(false);
    setChatTitle(t(m.newChat));
    setNotice(null);
    boundReportRef.current = null;
    // Новый чат — в «Авто» (режим по умолчанию, 6al.26).
    restoreMode('auto');
    inputRef.current?.focus();
  };

  // «Изменить с ИИ» на странице сохранённого отчёта: новая беседа в режиме
  // «Отчёт», привязанная к отчёту; параметр адреса снимается сразу.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get(AI_REPORT_PARAM);
    if (!ref) return;
    params.delete(AI_REPORT_PARAM);
    const rest = params.toString();
    navigateRef.current({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
    if (!UUID.test(ref)) return;
    loadedRef.current = true; // не подменять беседу последней из истории
    startNew();
    boundReportRef.current = ref;
    setMode('report');
    setOpen(true);
    setNotice(t(m.reportEditNotice));
  }, [location.search]);

  const switchTo = (id: string) => {
    if (id === chatIdRef.current) {
      setHistoryOpen(false);
      return;
    }
    stop();
    clearError();
    setFailure(null);
    boundReportRef.current = null;
    getChat(id).then(
      (detail) => {
        setChatId(detail.id);
        setPinned(detail.pinned);
        restoreMode(detail.mode);
        showChat(detail.messages);
        setPending(detail.status === 'running' ? { since: Date.now() - (detail.elapsed_s ?? 0) * 1000 } : null);
            setCanvas(null);
        setChatTitle(shownTitle(detail.title));
        setHistoryOpen(false);
      },
      (reason: unknown) => setHistoryError(t(m.chatOpenFailed, { reason: errorText(reason) })),
    );
  };

  const togglePin = () => {
    const id = chatIdRef.current;
    if (id) pinChat(id, !pinned);
  };
  const pinChat = (id: string, value: boolean) =>
    patchChat(id, { pinned: value }).then(
      (next) => {
        if (id === chatIdRef.current) setPinned(next.pinned);
        setChats((list) => list?.map((item) => (item.id === id ? next : item)) ?? list);
      },
      (reason: unknown) => setNotice(t(m.pinFailed, { reason: errorText(reason) })),
    );
  const renameChat = (id: string, title: string) =>
    patchChat(id, { title }).then(
      (next) => {
        if (id === chatIdRef.current) setChatTitle(shownTitle(next.title));
        setChats((list) => list?.map((item) => (item.id === id ? next : item)) ?? list);
      },
      (reason: unknown) => setNotice(t(m.renameFailed, { reason: errorText(reason) })),
    );
  const removeChat = (id: string) =>
    deleteChat(id).then(
      () => {
        setChats((list) => list?.filter((item) => item.id !== id) ?? list);
        if (id === chatIdRef.current) startNew();
      },
      (reason: unknown) => setNotice(t(m.deleteFailed, { reason: errorText(reason) })),
    );
  const removeCurrent = () => {
    const id = chatIdRef.current;
    if (id) void removeChat(id);
    else startNew();
  };

  const openHistory = () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    refreshList().catch((reason: unknown) => setHistoryError(errorText(reason)));
  };

  /** Вопрос; `override` — разовый явный режим (повтор решения «Авто»). */
  /** Принять картинки (скрепка, вставка, перетаскивание): формат, число, размер. */
  const attach = async (files: File[]) => {
    if (!files.length) return;
    const { accepted, problem } = screenImages(files, attachments.length);
    setNotice(problem);
    if (!accepted.length) return;
    try {
      const added = await toAttachments(accepted);
      setAttachments((current) => [...current, ...added].slice(0, 3));
    } catch {
      setNotice(t(m.imageReadFailed));
    }
  };
  const submit = async (text: string, override?: { mode: ExplicitMode; rerouted_from?: string }, files: Attachment[] = []) => {
    // Без текста, но с картинкой — вопрос по умолчанию (иначе нечего классифицировать).
    const value = text.trim() || (files.length ? t(m.screenshotQuestion) : '');
    if (!value || busy || pending) return;
    clearError();
    setFailure(null);
    setStalled(false);
    userStoppedRef.current = false;
    streamFinishedRef.current = true;
    lastActivityRef.current = Date.now();
    setInput('');
    setAttachments([]);
    setHistoryOpen(false);
    if (!chatIdRef.current) {
      try {
        const created = await createChat(modeRef.current);
        setChatId(created.id);
        setPinned(created.pinned);
        setChatTitle(shownTitle(created.title));
        setNotice(null);
      } catch {
        // Ответ всё равно придёт — просто не сохранится в истории.
        setNotice(t(m.historyDown));
      }
    }
    turnRef.current = { started: Date.now(), message: null };
    setThought(null);
    void sendMessage(
      files.length ? { text: value, files: files.map(({ id: _id, ...part }) => part) } : { text: value },
      override ? { body: override } : undefined,
    );
  };
  /** «изменить» у решения «Авто»: тот же вопрос в явном режиме. */
  const retryIn = (index: number, next: ExplicitMode) => {
    const question = messages
      .slice(0, index)
      .reverse()
      .find((item) => item.role === 'user');
    const text = question?.parts.map((p) => (p.type === 'text' ? p.text : '')).join('').trim();
    if (!text) return;
    if (next === 'report') setExpanded(true);
    void submit(text, { mode: next, rerouted_from: metaOf(messages[index]!).auto?.intent });
  };
  /** «Уточнить» во время ответа: поправка уходит серверу, он добавит её перед следующим шагом. */
  const clarify = async () => {
    const text = input.trim();
    const id = chatIdRef.current;
    if (!text || !id) return;
    setInput('');
    try {
      const { queued } = await clarifyChat(id, text);
      if (queued) setNotice(t(m.clarifyAccepted));
      else {
        setInput(text);
        setNotice(t(m.clarifyTooLate));
      }
    } catch (reason) {
      setInput(text);
      setNotice(t(m.clarifyFailed, { reason: errorText(reason) }));
    }
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void (busy ? clarify() : submit(input, undefined, attachments));
  };
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    void attach(files);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void (busy ? clarify() : submit(input, undefined, attachments));
    }
  };

  const stepsOf = (message: UIMessage): WorkStep[] => {
    const timing = metaOf(message).timing ?? {};
    return message.parts.filter(isWork).map((part) => {
      const live = timesRef.current.get(part.toolCallId);
      const stored = timing[part.toolCallId];
      const ms =
        live?.end !== undefined
          ? live.end - live.start
          : stored && stored.length >= 2 && typeof stored[0] === 'number' && typeof stored[1] === 'number'
            ? stored[1] - stored[0]
            : null;
      return { part, ms };
    });
  };
  const totalOf = (message: UIMessage): number | null =>
    totals[message.id] ?? (typeof metaOf(message).duration_ms === 'number' ? (metaOf(message).duration_ms ?? null) : null);

  const textOf = (message: UIMessage) =>
    message.parts.map((p) => (p.type === 'text' ? p.text : '')).join('').trim();
  const copyAnswer = (message: UIMessage) =>
    navigator.clipboard.writeText(textOf(message)).then(
      () => setNotice(t(m.copied)),
      () => setNotice(t(m.copyFailed)),
    );
  /** «Повторить»: тот же вопрос, что стоял перед этим ответом. */
  const repeatAnswer = (index: number) => {
    const question = messages
      .slice(0, index)
      .reverse()
      .find((item) => item.role === 'user');
    const text = question ? textOf(question) : '';
    if (text) void submit(text);
  };

  const families = useMemo(() => reportFamilies(messages), [messages]);
  const canvasSize = useRef<PanelSize | null>(null);
  const openCanvas = (next: { family: string | null; saved: string | null }) => {
    setHistoryOpen(false);
    setCanvas(next);
    // Отчёт открывается во весь экран (решение владельца 05.10): отчёту —
    // не меньше двух третей ширины, чтобы числа не сжимались. Закрыли холст —
    // панель возвращается к прежнему размеру.
    if (size !== 'focus') {
      canvasSize.current = size;
      setSize('focus');
    }
  };
  const closeCanvas = () => {
    setCanvas(null);
    if (canvasSize.current && size === 'focus') setSize(canvasSize.current);
    canvasSize.current = null;
  };
  const canvasFamily = canvas?.family ? (families.get(canvas.family) ?? null) : null;

  const { pathname } = useLocation();
  const screen = useMemo(() => pageForPath(pathname), [pathname]);
  // Подсказки пустой беседы — вопросы блоков текущего экрана из каталога ai-surface.
  const [hints, setHints] = useState<string[]>([]);
  const screenId = screen?.id ?? null;
  useEffect(() => {
    let alive = true;
    setHints([]);
    // Вопросы блоков экрана — знания ИИ по-русски (каталог ai-surface не переводится,
    // ADR-0019): на языках перевода остаются переведённые примеры режима.
    if (open && screenId && isSourceLocale()) screenQuestions(screenId).then((list) => alive && setHints(list), () => undefined);
    return () => {
      alive = false;
    };
  }, [open, screenId]);

  const lastMessage = messages[messages.length - 1];
  messagesRef.current = messages;
  const orphanQuestion = !busy && !pending && !failure && !error && lastMessage?.role === 'user';
  const retryLast = () => {
    const index = messages.map((m) => m.role).lastIndexOf('user');
    const question = messages[index]?.parts.map((p) => (p.type === 'text' ? p.text : '')).join('').trim();
    if (!question) return;
    setMessages(messages.slice(0, index));
    void submit(question);
  };
  const liveId = busy && lastMessage?.role === 'assistant' ? lastMessage.id : null;

  // Все хуки выше: вышедшему участнику панель не рисуется.
  if (!signedIn) return null;

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="agent-fab focus-ring"
          onClick={() => setOpen(true)}
          aria-label={t(m.openFabAria)}
        >
          <Icon icon={Sparkles} size={20} />
          <span>{t(m.openFab)}</span>
        </button>
      ) : null}
      {open ? (
        <aside
          className={`agent-panel${expanded ? ' agent-panel--expanded' : ''}${size === 'wide' ? ' agent-panel--wide' : ''}${resizing ? ' agent-panel--resizing' : ''}`}
          aria-label={t(m.panelAria)}
          style={size !== 'side' ? undefined : ({ '--agent-panel-width': `${panelWidth.width}px` } as CSSProperties)}
        >
          {size !== 'side' ? null : (
            <PanelResizeHandle
              width={panelWidth.width}
              max={panelWidth.max}
              onChange={panelWidth.setWidth}
              onReset={panelWidth.reset}
              onDragging={setResizing}
            />
          )}
          <PanelHeader
            title={chatTitle}
            hasChat={Boolean(chatId)}
            pinned={pinned}
            size={size}
            drawerOpen={historyOpen}
            onSize={(next) => {
              autoWideRef.current = false;
              if (next === 'side' && canvas) {
                setCanvas(null);
                canvasSize.current = null;
              }
              if (next === size && next !== 'side') {
                // Повторное нажатие — возврат: из «Фокуса» к прежней ширине,
                // из «Широкой» к ширине по умолчанию.
                if (next === 'wide') panelWidth.reset();
                if (canvas) {
                  setCanvas(null);
                  canvasSize.current = null;
                }
                setSize('side');
                return;
              }
              setSize(next);
            }}
            onDrawer={openHistory}
            onRename={(title) => chatId && void renameChat(chatId, title)}
            onTogglePin={togglePin}
            onDelete={removeCurrent}
            onClose={() => setOpen(false)}
          />

          <div className="agent-panel__body">
          <div className="agent-convo">
          <div className="agent-panel__list" ref={listRef} aria-live="polite">
            {(
              <>
                {messages.length === 0 ? (
                  <div className="agent-empty">
                    <p>{t(m.empty)}</p>
                    <div className="agent-empty__chips">
                      {(hints.length > 0 ? hints : (SUGGESTIONS[mode] ?? SUGGESTIONS.hint ?? []).map((message) => t(message))).map((text) => (
                        <button key={text} type="button" className="agent-chip focus-ring" onClick={() => void submit(text)}>
                          {text}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {messages.map((message, messageIndex) => {
                  const meta = metaOf(message);
                  const next = messages[messageIndex + 1];
                  const reply =
                    next?.role === 'user'
                      ? next.parts.map((p) => (p.type === 'text' ? p.text : '')).join('').trim() || null
                      : null;
                  const live = message.id === liveId;
                  const steps = message.role === 'assistant' ? stepsOf(message) : [];
                  const lastPart = message.parts[message.parts.length - 1];
                  return (
                    <div key={message.id} className={`agent-msg agent-msg--${message.role}`}>
                      {message.role === 'assistant' ? (
                        <RouteCaption meta={meta} onRetry={live || busy ? undefined : (next) => retryIn(messageIndex, next)} />
                      ) : null}
                      {message.role === 'assistant' && !live ? <WorkLog steps={steps} totalMs={totalOf(message)} /> : null}
                      {message.parts.map((part, index) => {
                        if (part.type === 'text') {
                          return message.role === 'assistant' ? (
                            <Streamdown
                              key={index}
                              className="agent-md"
                              // Картинок в ответе нет: `![](https://x/?d=…)` из
                              // внедрённого в данные текста браузер загрузил бы сам
                              // и вынес результат запроса наружу (ревью 23.09).
                              disallowedElements={['img']}
                              // Кнопки «копировать / скачать / во весь экран» на таблицах
                              // и коде не нужны: полноэкранный режим есть у самой панели.
                              controls={false}
                              // Высоту таблицы и кода держит обёртка streamdown
                              // (inline max-height); прокрутку внутри неё даёт
                              // agent.css: классы Tailwind из streamdown у нас не
                              // собираются (префикс tw:), и без неё длинная
                              // таблица наезжала на блоки ниже (6al.39). Пока
                              // ответ пишется, потолка нет: streamdown иначе
                              // прокручивает таблицу к низу и оставляет её там.
                              tableMaxHeight={live ? 'none' : TABLE_MAX_HEIGHT}
                              codeBlockMaxHeight={live ? 'none' : TABLE_MAX_HEIGHT}
                              isAnimating={live}
                            >
                              {part.text}
                            </Streamdown>
                          ) : (
                            <p key={index} className="agent-msg__text">
                              {part.text}
                            </p>
                          );
                        }
                        if (part.type === 'file' && part.mediaType.startsWith('image/')) {
                          return part.url.startsWith('data:') ? (
                            <img key={index} className="agent-msg__image" src={part.url} alt={part.filename ?? t(m.attachedImage)} />
                          ) : (
                            <p key={index} className="agent-msg__note">{part.filename ? t(m.imageNoteNamed, { name: part.filename }) : t(m.imageNote)}</p>
                          );
                        }
                        if (part.type !== 'dynamic-tool') return null;
                        if (isReportPart(part)) {
                          const out = (part as DynamicToolUIPart).output as Report;
                          const family = families.get(familyKey(out, part.toolCallId));
                          // В ленте — одна карточка на отчёт: у прежних версий её нет.
                          if (!family || family.toolCallId !== part.toolCallId) return null;
                          return (
                            <div key={part.toolCallId} className="agent-artifact">
                              <Icon icon={FileChartColumn} size={18} />
                              <div className="agent-artifact__text">
                                <span className="agent-artifact__title">{out.title}</span>
                                <span className="agent-artifact__meta">{t(m.artifactVersion, { n: out.saved_report?.version ?? out.revision ?? 1 })}</span>
                              </div>
                              <button type="button" className="btn btn--sm focus-ring" onClick={() => openCanvas({ family: family.key, saved: null })}>
                                {t(m.artifactOpen)}
                              </button>
                            </div>
                          );
                        }
                        if (part.toolName === 'ui_tour_plan') {
                          // Ошибка проверки — модели на исправление, участнику не показывается.
                          if (part.state !== 'output-available' || !isTour(part.output)) return null;
                          const plan = part.output;
                          const id = part.toolCallId;
                          return (
                            <TourCard
                              key={id}
                              tour={plan}
                              position={tour?.position.id === id ? tour.position : null}
                              onGo={(step) => goTour(plan, id, step)}
                              onClose={closeTour}
                            />
                          );
                        }
                        if (part.toolName === 'ui_ask_choice') {
                          if (part.state !== 'output-available' || !isChoice(part.output)) return null;
                          return (
                            <ChoiceCard
                              key={part.toolCallId}
                              choice={part.output}
                              answered={reply}
                              disabled={busy}
                              onPick={(option) => void submit(option)}
                            />
                          );
                        }
                        return UI_TOOL_NAMES.has(part.toolName) ? <UiToolCard key={part.toolCallId} part={part} /> : null;
                      })}
                      {message.role === 'assistant' && !live && textOf(message) ? (
                        <div className="agent-msg__tools" role="group" aria-label={t(m.answerTools)}>
                          <button type="button" className="btn btn--ghost btn--sm focus-ring" aria-label={t(m.copyAnswerAria)} title={t(m.copyAnswerHint)} onClick={() => void copyAnswer(message)}>
                            <Icon icon={Copy} size={16} />
                          </button>
                          <button type="button" className="btn btn--ghost btn--sm focus-ring" aria-label={t(m.repeatAria)} title={t(m.repeatHint)} disabled={busy || Boolean(pending)} onClick={() => repeatAnswer(messageIndex)}>
                            <Icon icon={RotateCcw} size={16} />
                          </button>
                        </div>
                      ) : null}
                      {live ? (
                        <StatusLine
                          steps={steps}
                          writing={lastPart?.type === 'text'}
                          startedAt={turnRef.current.started}
                          thought={thought}
                        />
                      ) : null}
                      {meta.interrupted && !live ? (
                        <p className="agent-msg__note">{t(m.answerInterrupted)}</p>
                      ) : null}
                    </div>
                  );
                })}
                {status === 'submitted' && lastMessage?.role !== 'assistant' ? (
                  <div className="agent-msg agent-msg--assistant">
                    <StatusLine steps={[]} writing={false} startedAt={turnRef.current.started} thought={thought} />
                  </div>
                ) : null}
                {pending ? (
                  <p className="agent-msg__note" role="status">
                    {t(m.stillPreparing, { seconds: Math.max(0, Math.round((nowTick - pending.since) / 1000)) })}
                  </p>
                ) : null}
                {busy && stalled ? <p className="agent-msg__note">{t(m.stalled)}</p> : null}
                {!busy && !pending && (failure || error || orphanQuestion) ? (
                  <div className="agent-error" role="alert">
                    {failure ?? (error ? t(m.failedWith, { reason: failureReason(error) }) : t(m.failedOrphan))}
                    {' · '}
                    <button type="button" className="btn btn--ghost btn--sm focus-ring" onClick={retryLast}>
                      {t(m.retry)}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <form className="agent-composer" onSubmit={onSubmit}>
            {notice ? <p className="agent-composer__notice">{notice}</p> : null}
            {tour && !historyOpen && !lastMessage?.parts.some((p) => p.type === 'dynamic-tool' && p.toolCallId === tour.position.id) ? (
              <TourBar
                tour={tour.plan}
                position={tour.position}
                onGo={(step) => goTour(tour.plan, tour.position.id, step)}
                onClose={closeTour}
              />
            ) : null}
            {screen && includePage ? (
              <div className="agent-composer__ctx">
                <span className="agent-ctx">
                  <span>{t(m.screenCtx, { title: screenTitle(screen.id, screen.title) })}</span>
                  <button type="button" className="focus-ring" onClick={() => setIncludePage(false)} aria-label={t(m.removeScreenAria)} title={t(m.removeScreenHint)}>
                    <Icon icon={X} size={16} />
                  </button>
                </span>
              </div>
            ) : null}
            <div
              className={`agent-composer__box${dragging ? ' agent-composer__box--drop' : ''}`}
              onDragOver={(event: DragEvent) => {
                if (Array.from(event.dataTransfer.types).includes('Files')) {
                  event.preventDefault();
                  setDragging(true);
                }
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event: DragEvent) => {
                setDragging(false);
                const files = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith('image/'));
                if (!files.length) return;
                event.preventDefault();
                void attach(files);
              }}
            >
              {attachments.length ? (
                <ul className="agent-attach" aria-label={t(m.attachedAria)}>
                  {attachments.map((item) => (
                    <li key={item.id} className="agent-attach__item">
                      <img src={item.url} alt={item.filename ?? t(m.pictureAlt)} />
                      <button
                        type="button"
                        className="agent-attach__remove focus-ring"
                        aria-label={t(m.removePicture, { name: item.filename ?? '' }).trim()}
                        onClick={() => setAttachments((current) => current.filter((other) => other.id !== item.id))}
                      >
                        <Icon icon={X} size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <textarea
                ref={inputRef}
                className="agent-composer__input"
                value={input}
                placeholder={busy ? t(m.inputBusy) : t(m.inputIdle)}
                rows={2}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                aria-label={t(m.inputAria)}
              />
              <div className="agent-composer__bar">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  hidden
                  aria-label={t(m.pickImages)}
                  onChange={(event) => {
                    void attach(Array.from(event.target.files ?? []));
                    event.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="agent-attach__add focus-ring"
                  aria-label={t(m.attachAria)}
                  title={t(m.attachHint)}
                  disabled={busy || attachments.length >= 3}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon icon={Paperclip} size={16} />
                </button>
                <ModePicker mode={mode} options={MODES.map((item) => ({ ...item, label: t(item.label), hint: t(item.hint) }))} disabled={busy} onChange={setMode} />
                {busy && chatId ? (
                  <button type="submit" className="btn btn--sm focus-ring" disabled={!input.trim()} title={t(m.clarifyHint)}>
                    {t(m.clarify)}
                  </button>
                ) : null}
                {busy ? (
                  <button type="button" className="agent-send agent-send--stop focus-ring" onClick={() => {
                      userStoppedRef.current = true;
                      // Сервер тоже прекращает генерацию (agroai-6al.60): иначе он дочитал бы ответ и потратил токены.
                      if (chatIdRef.current) void stopChat(chatIdRef.current).catch(() => undefined);
                      stop();
                    }} aria-label={t(m.stop)} title={t(m.stop)}>
                    <Icon icon={Square} size={16} />
                  </button>
                ) : (
                  <button type="submit" className="agent-send focus-ring" disabled={!input.trim() && !attachments.length} aria-label={t(m.send)} title={t(m.sendHint)}>
                    <Icon icon={ArrowUp} size={18} />
                  </button>
                )}
              </div>
            </div>
          </form>
          </div>
          {canvas ? (
            <Suspense fallback={null}>
              <ReportCanvas
                live={canvasFamily?.report ?? null}
                liveRef={canvasFamily && chatId ? { chatId, toolCallId: canvasFamily.toolCallId } : null}
                openedId={canvas.saved}
                onClose={closeCanvas}
                onOpenSaved={(id) => setCanvas({ family: null, saved: id })}
              />
            </Suspense>
          ) : null}
          </div>
          {historyOpen ? (
            <ChatDrawer
              tab={drawerTab}
              onTab={setDrawerTab}
              onClose={() => setHistoryOpen(false)}
              chats={chats}
              chatId={chatId}
              error={historyError}
              ttlDays={ttlDays}
              user={user}
              onNew={startNew}
              onOpenChat={switchTo}
              onRenameChat={(id, title) => void renameChat(id, title)}
              onPinChat={(id, value) => void pinChat(id, value)}
              onDeleteChat={(id) => void removeChat(id)}
              onOpenReport={(id) => openCanvas({ family: null, saved: id })}
            />
          ) : null}
        </aside>
      ) : null}
    </>
  );
}
